import { useState } from 'react';

interface CashReconciliationModalProps {
  onClose: () => void;
  onConfirm: (countedAmount: number, varianceReason: string) => void;
  expectedBalance: number;
  shiftDetails: {
    openingBalance: number;
    cashSales: number;
    cashRefunds: number;
    paidIn: number;
    paidOut: number;
  };
}

export default function CashReconciliationModal({ 
  onClose, 
  onConfirm, 
  expectedBalance,
  shiftDetails 
}: CashReconciliationModalProps) {
  const [countedAmount, setCountedAmount] = useState('');
  const [varianceReason, setVarianceReason] = useState('');
  const [error, setError] = useState('');
  const [step, setStep] = useState<'count' | 'variance'>('count');

  const variance = countedAmount ? parseFloat(countedAmount) - expectedBalance : 0;
  const hasVariance = Math.abs(variance) > 0.01;
  const isLargeVariance = Math.abs(variance) > 50;

  const handleContinue = () => {
    const numAmount = parseFloat(countedAmount);

    if (!countedAmount || numAmount < 0) {
      setError('Please enter a valid counted amount');
      return;
    }

    if (hasVariance) {
      setStep('variance');
    } else {
      onConfirm(numAmount, '');
    }
  };

  const handleConfirm = () => {
    if (hasVariance && !varianceReason.trim()) {
      setError('Please provide a reason for the variance');
      return;
    }

    if (isLargeVariance && !varianceReason.trim()) {
      setError('Large variance requires detailed explanation');
      return;
    }

    onConfirm(parseFloat(countedAmount), varianceReason);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <i className="ri-calculator-line text-xl text-gray-700"></i>
            <h2 className="text-lg font-semibold text-gray-900">Cash Reconciliation - Shift Close</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {step === 'count' && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-blue-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="text-sm font-medium text-blue-900">Shift Summary</p>
                    <p className="text-xs text-blue-700 mt-1">Review your shift activity before counting cash</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Opening Balance:</span>
                  <span className="text-sm font-medium text-gray-900">${shiftDetails.openingBalance.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-green-600">+ Cash Sales:</span>
                  <span className="text-sm font-medium text-green-600">${shiftDetails.cashSales.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-red-600">- Cash Refunds:</span>
                  <span className="text-sm font-medium text-red-600">${shiftDetails.cashRefunds.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-green-600">+ Paid In:</span>
                  <span className="text-sm font-medium text-green-600">${shiftDetails.paidIn.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-red-600">- Paid Out:</span>
                  <span className="text-sm font-medium text-red-600">${shiftDetails.paidOut.toFixed(2)}</span>
                </div>
                <div className="pt-3 border-t border-gray-300 flex justify-between items-center">
                  <span className="text-base font-semibold text-gray-900">Expected Balance:</span>
                  <span className="text-xl font-bold text-gray-900">${expectedBalance.toFixed(2)}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Count Physical Cash <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-xl">$</span>
                  <input
                    type="number"
                    value={countedAmount}
                    onChange={(e) => {
                      setCountedAmount(e.target.value);
                      setError('');
                    }}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="w-full pl-10 pr-3 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-2xl font-bold"
                    autoFocus
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">Count all bills and coins in the drawer</p>
              </div>

              {countedAmount && parseFloat(countedAmount) >= 0 && (
                <div className={`rounded-lg p-4 ${hasVariance ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Variance:</span>
                    <span className={`text-2xl font-bold ${variance > 0 ? 'text-green-600' : variance < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                      {variance > 0 ? '+' : ''}{variance.toFixed(2)}
                    </span>
                  </div>
                  {hasVariance && (
                    <p className="text-xs text-gray-600 mt-2">
                      {variance > 0 ? 'Cash over expected amount' : 'Cash short of expected amount'}
                    </p>
                  )}
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-500"></i>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
            </>
          )}

          {step === 'variance' && (
            <>
              <div className={`${isLargeVariance ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'} border rounded-lg p-4`}>
                <div className="flex items-start gap-3">
                  <i className={`${isLargeVariance ? 'ri-error-warning-fill text-red-600' : 'ri-alert-line text-yellow-600'} text-xl mt-0.5`}></i>
                  <div>
                    <p className={`text-sm font-medium ${isLargeVariance ? 'text-red-900' : 'text-yellow-900'}`}>
                      {isLargeVariance ? 'Large Variance Detected' : 'Variance Detected'}
                    </p>
                    <p className={`text-xs ${isLargeVariance ? 'text-red-700' : 'text-yellow-700'} mt-1`}>
                      {isLargeVariance 
                        ? 'This variance exceeds $50 and requires supervisor approval and detailed explanation'
                        : 'Please provide a reason for the cash variance'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Expected</p>
                    <p className="text-lg font-semibold text-gray-900">${expectedBalance.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Counted</p>
                    <p className="text-lg font-semibold text-gray-900">${parseFloat(countedAmount).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Variance</p>
                    <p className={`text-lg font-bold ${variance > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {variance > 0 ? '+' : ''}{variance.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Variance Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={varianceReason}
                  onChange={(e) => {
                    setVarianceReason(e.target.value);
                    setError('');
                  }}
                  placeholder="Explain the reason for the variance (e.g., counting error, missing transaction, incorrect change given...)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none text-sm"
                  rows={4}
                  maxLength={500}
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">{varianceReason.length}/500 characters</p>
              </div>

              {isLargeVariance && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <i className="ri-shield-user-line text-red-500 text-lg mt-0.5"></i>
                  <div>
                    <p className="text-sm font-medium text-red-800">Supervisor Approval Required</p>
                    <p className="text-xs text-red-600 mt-1">A supervisor must review and approve this reconciliation before shift close</p>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-500"></i>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex gap-3 p-4 border-t bg-gray-50 sticky bottom-0">
          {step === 'variance' && (
            <button
              onClick={() => setStep('count')}
              className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium text-gray-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Back
            </button>
          )}
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium text-gray-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={step === 'count' ? handleContinue : handleConfirm}
            className="flex-1 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            {step === 'count' ? 'Continue' : 'Complete Reconciliation'}
          </button>
        </div>
      </div>
    </div>
  );
}
